/**
 * GreenCharge driver-app journey telemetry.
 *
 *   startJourney({ entry, stationId })   at the QR landing
 *   step(name)                           on every wizard step change
 *   track(name, { result, reason, metadata })
 *   endJourney(result)                   receipt shown / blocked / superseded
 *
 * Events go to POST {VITE_API_URL}/telemetry/v1/events in small batches and
 * are validated against the server catalogue there: anything undeclared is
 * dropped server-side. This module never reads form values, DOM text, PINs,
 * plates, emails or tokens. VITE_PRODUCT_TELEMETRY_ENABLED=false disables it.
 */

const API_BASE = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');
const RELEASE = (import.meta.env.VITE_CM_RELEASE || 'unknown').slice(0, 40);
let ENABLED = String(import.meta.env.VITE_PRODUCT_TELEMETRY_ENABLED ?? 'true') !== 'false'
  && import.meta.env.MODE !== 'test';
/** Tests only. */
export function _enableForTests(on = true) { ENABLED = on; installed = false; queue = []; }

const JOURNEY_KEY = 'cm.journey.charging';
const SESSION_KEY = 'cm.client_session';
const IDLE_MS = 30 * 60 * 1000;
const FLUSH_MS = 4000;
const MAX_BATCH = 20;
const PRE_START_STEPS = new Set(['qr_landing', 'charger_screen', 'account_choice', 'connector_select', 'plate', 'pin', 'amount', 'payment', 'connect']);

function uuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const b = new Uint8Array(16);
  globalThis.crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
function hex(n) {
  const b = new Uint8Array(n);
  globalThis.crypto.getRandomValues(b);
  return [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

function read(key) {
  try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { return null; }
}
function write(key, value) {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ }
}

function clientSession() {
  const now = Date.now();
  let s = read(SESSION_KEY);
  if (!s || now - s.lastActivity > IDLE_MS) s = { id: `cs_${uuid()}`, seq: 0 };
  s.lastActivity = now;
  write(SESSION_KEY, s);
  return s;
}

function activeJourney() {
  const j = read(JOURNEY_KEY);
  if (!j || j.ended || Date.now() - j.lastActivity > IDLE_MS) return null;
  return j;
}

let queue = [];
let timer = null;
let installed = false;

function platform() {
  try {
    const cap = globalThis.Capacitor;
    const p = cap?.getPlatform?.();
    return p === 'ios' || p === 'android' ? p : 'web';
  } catch { return 'web'; }
}

function send(events, { beacon = false } = {}) {
  if (!events.length) return;
  const body = JSON.stringify({ platform: platform(), events });
  const url = `${API_BASE}/telemetry/v1/events`;
  try {
    if (beacon && navigator.sendBeacon) {
      // text/plain is CORS-safelisted, so the beacon needs no preflight.
      navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
      return;
    }
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true, credentials: 'omit' })
      .catch(() => {});
  } catch { /* telemetry never breaks the app */ }
}

export function flush(opts) {
  if (timer) { clearTimeout(timer); timer = null; }
  const batch = queue.splice(0, queue.length);
  for (let i = 0; i < batch.length; i += MAX_BATCH) send(batch.slice(i, i + MAX_BATCH), opts);
}

/**
 * Record an event. `metadata` must use catalogue keys; values that are not
 * bounded enums / numbers / booleans are rejected server-side anyway.
 */
export function track(eventName, { result, reason, metadata, traceId } = {}) {
  if (!ENABLED) return;
  try {
    const s = clientSession();
    s.seq += 1;
    write(SESSION_KEY, s);
    const j = activeJourney();
    if (j) { j.lastActivity = Date.now(); write(JOURNEY_KEY, j); }
    queue.push({
      event_name: eventName,
      result, reason, metadata,
      journey_id: j?.id || null,
      flow: j ? 'charging' : null,
      client_session_id: s.id,
      seq: s.seq,
      timestamp: new Date().toISOString(),
      client_release: RELEASE,
      route: typeof location !== 'undefined' ? location.pathname : null,
      trace_id: traceId,
    });
    if (queue.length >= MAX_BATCH) flush();
    else if (!timer) timer = setTimeout(() => flush(), FLUSH_MS);
  } catch { /* never throws */ }
}

export function startJourney({ entry = 'qr', stationId = null } = {}) {
  if (!ENABLED) return null;
  const existing = activeJourney();
  if (existing && existing.stationId === stationId) {
    existing.lastActivity = Date.now();
    write(JOURNEY_KEY, existing);
    return existing.id;
  }
  if (existing) endJourney('superseded');
  const j = { id: `jny_${uuid()}`, stationId, startedAt: Date.now(), lastActivity: Date.now(), lastStep: null, ended: false };
  write(JOURNEY_KEY, j);
  track('journey.flow.started', { metadata: { entry } });
  return j.id;
}

/** Start a journey for a direct (non-QR) visit if none is active. */
export function ensureJourney(stationId) {
  if (!ENABLED) return;
  if (!activeJourney()) startJourney({ entry: 'direct', stationId });
}

export function step(name) {
  if (!ENABLED) return;
  const j = activeJourney();
  if (!j || j.lastStep === name) return;
  j.lastStep = name;
  write(JOURNEY_KEY, j);
  track('journey.step.reached', { metadata: { step: name } });
}

export function endJourney(result) {
  if (!ENABLED) return;
  const j = activeJourney();
  if (!j) return;
  track('journey.flow.ended', { result, metadata: { last_step: j.lastStep || 'unknown' } });
  j.ended = true;
  write(JOURNEY_KEY, j);
  flush();
}

/** Correlation headers for requests to the GreenCharge API only. */
export function correlationHeaders(url) {
  if (!ENABLED) return null;
  const target = String(url || '');
  const sameApi = target.startsWith(API_BASE) || target.startsWith('/api') || (!/^https?:/i.test(target));
  if (!sameApi) return null;
  const headers = { traceparent: `00-${hex(16)}-${hex(8)}-01` };
  const j = activeJourney();
  if (j) {
    headers['X-CM-Journey-Id'] = j.id;
    headers['X-CM-Flow'] = 'charging';
  }
  const s = read(SESSION_KEY);
  if (s?.id) headers['X-CM-Session-Id'] = s.id;
  return headers;
}

/** Wire page lifecycle and global error capture. Idempotent. */
export function installTelemetry(axios) {
  if (!ENABLED || installed) return;
  installed = true;
  axios.interceptors.request.use((config) => {
    try {
      const h = correlationHeaders(config.url?.startsWith('http') ? config.url : `${config.baseURL || ''}${config.url || ''}`);
      if (h) {
        config.headers = config.headers || {};
        for (const [k, v] of Object.entries(h)) if (!config.headers[k]) config.headers[k] = v;
      }
    } catch { /* ignore */ }
    return config;
  });
  axios.interceptors.response.use((r) => r, (error) => {
    try {
      const status = error?.response?.status;
      const url = String(error?.config?.url || '');
      if (status && !url.includes('/telemetry/')) {
        const tp = error?.config?.headers?.traceparent;
        track('api.request.failed', {
          result: 'failed',
          reason: typeof error.response.data?.error === 'string' ? error.response.data.error : `HTTP_${status}`,
          metadata: { route: url.replace(/^https?:\/\/[^/]+/, '').split('?')[0], http_status: status },
          traceId: typeof tp === 'string' ? tp.split('-')[1] : undefined,
        });
      }
    } catch { /* ignore */ }
    return Promise.reject(error);
  });
  window.addEventListener('error', (e) => track('error.client.unhandled', { metadata: { error_kind: e?.error?.name || 'Error' } }));
  window.addEventListener('unhandledrejection', (e) => track('error.client.unhandled', { metadata: { error_kind: e?.reason?.name || 'UnhandledRejection' } }));
  const leave = () => {
    const j = activeJourney();
    // Leaving before charging started is the abandonment signal; leaving
    // while charging is normal (drivers walk away) and is not recorded.
    if (j && !j.abandonSent && PRE_START_STEPS.has(j.lastStep || 'qr_landing')) {
      track('journey.flow.ended', { result: 'abandoned', metadata: { last_step: j.lastStep || 'qr_landing' } });
      // Not marked ended: coming back within 30 min resumes the journey.
      j.abandonSent = true;
      write(JOURNEY_KEY, j);
    }
    flush({ beacon: true });
  };
  window.addEventListener('pagehide', leave);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush({ beacon: true }); });
}

// Wizard step number → catalogue step name (src/constants/steps.js values).
export const STEP_NAMES = {
  1: 'charger_screen', 1.25: 'account_choice', 1.5: 'connector_select', 2: 'plate', 2.5: 'pin', 2.75: 'access_result',
  3: 'amount', 4: 'payment', 5: 'connect', 6: 'charging', 7: 'disconnect', 8: 'receipt',
};

export function amountBand(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return 'full';
  if (n < 10) return 'lt_10';
  if (n < 25) return '10_25';
  if (n < 50) return '25_50';
  if (n < 100) return '50_100';
  return 'gte_100';
}
